<template>
    <!-- <div id="app"> -->
    <div class="container">
        <header>
            <div class="header-content">
                <div class="logo">
                    <i class="fas fa-book-open logo-icon"></i>
                    <h1>教材目录树题库系统</h1>
                </div>

                <div class="header-actions">
                    <div class="textbook-selector">
                        <select v-model="currentTextbook">
                            <option v-for="textbook in textbooks" :value="textbook.id">
                                {{ textbook.name }}
                            </option>
                        </select>
                    </div>

                    <button class="btn" @click="showBuildPaperDialog">
                        <i class="fas fa-file-alt"></i>
                        组卷
                    </button>
                </div>
            </div>
        </header>

        <aside class="sidebar">
            <div class="textbook-title">
                <i class="fas fa-book"></i>
                {{ getTextbookName(currentTextbook) }} 目录
            </div>

            <div class="tree-container">
                <div v-for="chapter in textbookChapters" :key="chapter.id" class="tree-node chapter">
                    <div class="node-content" :class="{ active: currentChapter === chapter.id }"
                        @click="toggleNode(chapter)">
                        <span class="toggle-icon" :class="{ expanded: chapter.expanded }">
                            <i class="fas fa-caret-right"></i>
                        </span>
                        <span class="node-icon">
                            <i class="fas fa-book"></i>
                        </span>
                        <span>{{ chapter.title }}</span>
                    </div>

                    <div class="children" :class="{ collapsed: !chapter.expanded }">
                        <div v-for="section in chapter.sections" :key="section.id" class="tree-node section">
                            <div class="node-content" :class="{ active: currentSection === section.id }"
                                @click="toggleNode(section)">
                                <span class="toggle-icon" :class="{ expanded: section.expanded }">
                                    <i class="fas fa-caret-right"></i>
                                </span>
                                <span class="node-icon">
                                    <i class="fas fa-folder"></i>
                                </span>
                                <span>{{ section.title }}</span>
                            </div>

                            <div class="children" :class="{ collapsed: !section.expanded }">
                                <div v-for="point in section.knowledgePoints" :key="point.id"
                                    class="tree-node knowledge-point">
                                    <div class="node-content" :class="{ active: currentPoint === point.id }"
                                        @click="selectPoint(point)">
                                        <span class="node-icon">
                                            <i class="fas fa-file-alt"></i>
                                        </span>
                                        <span>{{ point.title }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </aside>

        <main class="main-content">
            <div class="filters">
                <div class="filter-group">
                    <span class="filter-label">题型：</span>
                    <select class="filter-select" v-model="filter.type">
                        <option value="all">全部题型</option>
                        <option value="single_choice">单选题</option>
                        <option value="multiple_choice">多选题</option>
                        <option value="fill_in">填空题</option>
                        <option value="essay">解答题</option>
                    </select>
                </div>

                <div class="filter-group">
                    <span class="filter-label">难度：</span>
                    <select class="filter-select" v-model="filter.difficulty">
                        <option value="all">全部难度</option>
                        <option value="容易">容易</option>
                        <option value="较易">较易</option>
                        <option value="适中">适中</option>
                        <option value="较难">较难</option>
                        <option value="困难">困难</option>
                    </select>
                </div>

                <div class="filter-group">
                    <span class="filter-label">年份：</span>
                    <select class="filter-select" v-model="filter.year">
                        <option value="all">全部年份</option>
                        <option v-for="year in years" :value="year">{{ year }}年</option>
                    </select>
                </div>

                <div class="filter-group">
                    <span class="filter-label">来源：</span>
                    <select class="filter-select" v-model="filter.source">
                        <option value="all">全部来源</option>
                        <option v-for="source in sources" :value="source">{{ source }}</option>
                    </select>
                </div>

                <div class="search-container">
                    <i class="fas fa-search"></i>
                    <input type="text" v-model="searchQuery" placeholder="搜索题目内容、答案或知识点..." />
                </div>
            </div>

            <div class="questions-container">
                <div v-if="paginatedQuestions.length === 0" class="empty-state">
                    <i class="fas fa-inbox"></i>
                    <h3>暂无相关题目</h3>
                    <p>请尝试调整筛选条件或选择其他章节</p>
                </div>

                <div v-else class="questions-list">
                    <div v-for="question in paginatedQuestions" :key="question.id" class="question-card">
                        <div class="question-header">
                            <div class="question-info">
                                <div class="question-title">题目 #{{ question.id }}</div>
                                <div class="question-meta">
                                    <span class="meta-item"
                                        :class="`difficulty-${getDifficultyClass(question.difficulty)}`">
                                        <i class="fas fa-brain"></i> {{ question.difficulty }}
                                    </span>
                                    <span class="meta-item">
                                        <i class="fas fa-calendar"></i> {{ question.year }}
                                    </span>
                                    <span class="meta-item source-badge" v-if="question.sourceType">
                                        <i class="fas fa-tag"></i> {{ question.sourceType }}
                                    </span>
                                    <span class="meta-item">
                                        <i class="fas fa-map-marker-alt"></i> {{ question.source }}
                                    </span>
                                </div>
                            </div>
                            <div class="question-meta">
                                <span class="meta-item">
                                    <i class="fas fa-book"></i> {{ question.chapter }}
                                </span>
                            </div>
                        </div>

                        <div class="question-content math-content" v-html="question.content"></div>

                        <div class="question-tags">
                            <span v-for="tag in question.tags" :key="tag" class="tag">
                                <i class="fas fa-hashtag"></i> {{ tag }}
                            </span>
                        </div>

                        <div class="question-actions">
                            <div class="action-buttons">
                                <button class="action-btn" @click="addToCollection(question)">
                                    <i class="fas fa-plus"></i> 加入组卷
                                </button>
                                <button class="action-btn">
                                    <i class="fas fa-star"></i> 收藏
                                </button>
                                <button class="action-btn">
                                    <i class="fas fa-download"></i> 下载
                                </button>
                            </div>
                            <button class="toggle-answer" @click="toggleAnswer(question)">
                                <i class="fas" :class="question.showAnswer ? 'fa-eye-slash' : 'fa-eye'"></i>
                                {{ question.showAnswer ? '隐藏答案' : '查看答案' }}
                            </button>
                        </div>

                        <div v-if="question.showAnswer" class="answer-section">
                            <div class="answer-title">
                                <i class="fas fa-check-circle"></i> 答案与解析
                            </div>
                            <div class="answer-content math-content">
                                <div><strong>答案：</strong> <span v-html="question.answer"></span></div>
                                <div v-if="question.explain" style="margin-top: 15px;">
                                    <strong>解析：</strong> <span v-html="question.explain"></span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="pagination" v-if="filteredQuestions.length > 0">
                    <button @click="prevPage" :disabled="currentPage === 1">
                        <i class="fas fa-chevron-left"></i> 上一页
                    </button>
                    <span class="page-info">第 {{ currentPage }} 页 / 共 {{ totalPages }} 页</span>
                    <button @click="nextPage" :disabled="currentPage === totalPages">
                        下一页 <i class="fas fa-chevron-right"></i>
                    </button>
                </div>
            </div>
        </main>
    </div>

    <!-- 组卷对话框 -->
    <div class="dialog-overlay" v-if="showPaperDialog" @click.self="showPaperDialog = false">
        <div class="dialog">
            <div class="dialog-header">
                <h3>组卷设置</h3>
                <button class="close-btn" @click="showPaperDialog = false">
                    <i class="fas fa-times"></i>
                </button>
            </div>
            <div class="dialog-body">
                <p>当前已选择 <strong>{{ selectedQuestions.length }}</strong> 道题目</p>
                <div class="form-group">
                    <label>试卷标题</label>
                    <input type="text" v-model="paperTitle" placeholder="请输入试卷标题">
                </div>
                <div class="form-group">
                    <label>试卷描述</label>
                    <textarea v-model="paperDescription" placeholder="请输入试卷描述"></textarea>
                </div>
            </div>
            <div class="dialog-footer">
                <button class="btn secondary" @click="showPaperDialog = false">取消</button>
                <button class="btn" @click="downloadPaper">生成试卷</button>
            </div>
        </div>
    </div>
    <!-- </div> -->
</template>

<script setup>
import { ref, onMounted, reactive, computed, watch } from 'vue'
import axios from 'axios'

import Sidebar from '../components/Sidebar.vue'
import FilterBar from '../components/FilterBar.vue'
import QuestionCard from '../components/QuestionCard.vue'
import Pagination from '../components/Pagination.vue'

const difficultyOrder = { "容易": 1, "较易": 2, "适中": 3, "较难": 4, "困难": 5 };

// const questions = ref([])
const page = ref(1)
// const pageSize = ref(10)
const total = ref(0)
const filters = ref({})
const selectedChapter = ref(null)
const paperList = ref([])

// 当前教材
const currentTextbook = ref(1);

// 教材数据
const textbooks = ref([
    { id: 1, name: "高中数学必修一" },
    { id: 2, name: "高中数学必修二" },
    { id: 3, name: "高中数学选修一" },
    { id: 4, name: "高中数学选修二" }
]);

// 教材章节目录树
const textbookChapters = ref([
    {
        id: 1,
        title: "第一章 集合与函数",
        expanded: true,
        sections: [
            {
                id: 11,
                title: "1.1 集合的概念与表示",
                expanded: true,
                knowledgePoints: [
                    { id: 111, title: "集合的概念" },
                    { id: 112, title: "集合的表示方法" },
                    { id: 113, title: "集合间的关系" }
                ]
            },
            {
                id: 12,
                title: "1.2 集合的基本运算",
                expanded: false,
                knowledgePoints: [
                    { id: 121, title: "交集与并集" },
                    { id: 122, title: "补集" },
                    { id: 123, title: "集合的综合应用" }
                ]
            },
            {
                id: 13,
                title: "1.3 函数的概念",
                expanded: false,
                knowledgePoints: [
                    { id: 131, title: "函数的定义" },
                    { id: 132, title: "函数的表示方法" },
                    { id: 133, title: "函数的基本性质" }
                ]
            }
        ]
    },
    {
        id: 2,
        title: "第二章 三角函数",
        expanded: false,
        sections: [
            {
                id: 21,
                title: "2.1 任意角的三角函数",
                expanded: false,
                knowledgePoints: [
                    { id: 211, title: "角的概念推广" },
                    { id: 212, title: "弧度制" },
                    { id: 213, title: "任意角三角函数" }
                ]
            },
            {
                id: 22,
                title: "2.2 三角函数的诱导公式",
                expanded: false,
                knowledgePoints: [
                    { id: 221, title: "诱导公式一" },
                    { id: 222, title: "诱导公式二" },
                    { id: 223, title: "诱导公式的应用" }
                ]
            },
            {
                id: 23,
                title: "2.3 三角函数的图像与性质",
                expanded: false,
                knowledgePoints: [
                    { id: 231, title: "正弦函数图像" },
                    { id: 232, title: "余弦函数图像" },
                    { id: 233, title: "正切函数图像" }
                ]
            }
        ]
    },
    {
        id: 3,
        title: "第三章 立体几何",
        expanded: false,
        sections: [
            {
                id: 31,
                title: "3.1 空间几何体",
                expanded: false,
                knowledgePoints: [
                    { id: 311, title: "柱体、锥体、台体" },
                    { id: 312, title: "球体" },
                    { id: 313, title: "三视图" }
                ]
            },
            {
                id: 32,
                title: "3.2 空间点、线、面的位置关系",
                expanded: false,
                knowledgePoints: [
                    { id: 321, title: "空间点线关系" },
                    { id: 322, title: "空间线面关系" },
                    { id: 323, title: "空间面面关系" }
                ]
            },
            {
                id: 33,
                title: "3.3 空间向量及其应用",
                expanded: false,
                knowledgePoints: [
                    { id: 331, title: "空间向量的运算" },
                    { id: 332, title: "空间向量的应用" },
                    { id: 333, title: "空间角与距离" }
                ]
            }
        ]
    }
]);

// 当前选中的章节和知识点
const currentChapter = ref(1);
const currentSection = ref(11);
const currentPoint = ref(111);

// 筛选条件
const searchQuery = ref('');
const filter = reactive({
    type: 'all',
    difficulty: 'all',
    year: 'all',
    source: 'all'
});
const currentPage = ref(1);
const pageSize = ref(6);

// 组卷相关
const showPaperDialog = ref(false);
const paperTitle = ref('高中数学试卷');
const paperDescription = ref('这是一份精心准备的高中数学试卷，包含各种题型，涵盖多个知识点');
const selectedQuestions = ref([]);

// 示例题库数据
const questions = ref([
    {
        id: 1,
        type: 'single_choice',
        content: '已知集合 \(A = \{x \mid x^2 - 3x + 2 = 0\}\)，则集合 A 的元素个数为',
        answer: 'B. 2',
        explain: '解方程：\(x^2 - 3x + 2 = 0\)，得 \((x-1)(x-2)=0\)，解得 \(x=1\) 或 \(x=2\)，所以集合 A 有两个元素。',
        difficulty: '容易',
        year: '2024',
        source: '北京市',
        sourceType: '高考题',
        tags: ['集合', '解方程'],
        chapter: '第一章 1.1',
        point: '集合的概念',
        showAnswer: false
    },
    {
        id: 2,
        type: 'single_choice',
        content: '已知向量\(\overrightarrow{a},\overrightarrow{b}\)满足\(\left| \overrightarrow{a} \right| = 1,\left| \overrightarrow{b} \right| = 2,\overrightarrow{a}\)与\(\overrightarrow{b}\)的夹角为\(\frac{2\pi}{3}\)，则\(\left| 2\overrightarrow{a} + \overrightarrow{b} \right| =\)',
        answer: 'B. 2',
        explain: '利用数量积定义及数量积的运算律求解：<br>\(\overrightarrow{a} \cdot \overrightarrow{b} = 1 \times 2 \times \cos\frac{2\pi}{3} = -1\)<br>\(|2\overset{\rightarrow}{a} + \overset{\rightarrow}{b}| = \sqrt{4{\overset{\rightarrow}{a}}^{2} + {\overset{\rightarrow}{b}}^{2} + 4\overset{\rightarrow}{a} \cdot \overset{\rightarrow}{b}} = \sqrt{4 + 4 - 4} = 2\)',
        difficulty: '较易',
        year: '2025',
        source: '重庆市',
        sourceType: '模拟题',
        tags: ['向量', '数量积', '模长计算'],
        chapter: '第三章 3.3',
        point: '空间向量的运算',
        showAnswer: false
    },
    {
        id: 3,
        type: 'fill_in',
        content: '函数 \(f(x) = \sqrt{x-2} + \frac{1}{x-3}\) 的定义域为________',
        answer: '\([2,3) \cup (3,+\infty)\)',
        explain: '需满足两个条件：<br>1. \(x-2 \geq 0 \Rightarrow x \geq 2\)<br>2. \(x-3 \neq 0 \Rightarrow x \neq 3\)<br>所以定义域为 \([2,3) \cup (3,+\infty)\)',
        difficulty: '较易',
        year: '2023',
        source: '浙江省',
        sourceType: '高考题',
        tags: ['函数', '定义域'],
        chapter: '第一章 1.3',
        point: '函数的定义',
        showAnswer: false
    },
    {
        id: 4,
        type: 'essay',
        content: '已知函数 \(f(x) = 2\sin x \cos x - \sqrt{3}\cos 2x\)<br>(1) 求函数的最小正周期；<br>(2) 求函数在区间 \([0, \pi]\) 上的最大值和最小值',
        answer: '(1) \(\pi\)<br>(2) 最大值为 1，最小值为 -2',
        explain: '(1) 化简：\(f(x) = \sin 2x - \sqrt{3}\cos 2x = 2\sin(2x - \frac{\pi}{3})\)<br>最小正周期为 \(\frac{2\pi}{2} = \pi\)<br><br>(2) 当 \(x \in [0, \pi]\) 时，\(2x - \frac{\pi}{3} \in [-\frac{\pi}{3}, \frac{5\pi}{3}]\)<br>最大值：\(2 \times 1 = 2\)<br>最小值：\(2 \times (-1) = -2\)',
        difficulty: '适中',
        year: '2024',
        source: '江苏省',
        sourceType: '高考题',
        tags: ['三角函数', '周期', '最值'],
        chapter: '第二章 2.3',
        point: '正弦函数图像',
        showAnswer: false
    },
    {
        id: 5,
        type: 'multiple_choice',
        content: '下列函数中，既是偶函数又在区间\((0, +\infty)\)上单调递增的是',
        answer: 'A. \(y = |x| + 1\) <br> C. \(y = \cos|x|\)',
        explain: 'A: \(y = |x| + 1\) 是偶函数，在\((0, +\infty)\)上单调递增，符合<br>B: \(y = -\log_2 |x|\) 是偶函数，但在\((0, +\infty)\)上单调递减<br>C: \(y = \cos|x|\) 是偶函数，在\((0, \pi)\)上单调递减<br>D: \(y = x^2 + 2x\) 不是偶函数',
        difficulty: '适中',
        year: '2023',
        source: '全国卷',
        sourceType: '高考题',
        tags: ['函数', '奇偶性', '单调性'],
        chapter: '第一章 1.3',
        point: '函数的基本性质',
        showAnswer: false
    },
    {
        id: 6,
        type: 'single_choice',
        content: '如图，在正方体 ABCD-A₁B₁C₁D₁ 中，E 为棱 BB₁ 的中点，则异面直线 AE 与 CD 所成角的正切值为',
        answer: 'C. \(\sqrt{2}\)',
        explain: '取 DD₁ 中点 F，连接 EF、AF，则 ∠AEF 即为所求角<br>设正方体棱长为 2，则 \(AE = \sqrt{5}\)，\(EF = \sqrt{2}\)，\(AF = \sqrt{6}\)<br>由余弦定理：\(\cos \angle AEF = \frac{AE^2 + EF^2 - AF^2}{2 \times AE \times EF} = -\frac{1}{\sqrt{10}}\)<br>∴ \(\tan \angle AEF = \sqrt{2}\)',
        difficulty: '较难',
        year: '2025',
        source: '湖北省',
        sourceType: '模拟题',
        tags: ['立体几何', '异面直线', '空间角'],
        chapter: '第三章 3.3',
        point: '空间角与距离',
        showAnswer: false
    }
]);

// 静态数据
const years = computed(() => {
    const uniqueYears = new Set(questions.value.map(q => q.year));
    return Array.from(uniqueYears).sort((a, b) => b - a);
});

const sources = computed(() => {
    const uniqueSources = new Set(questions.value.map(q => q.source));
    return Array.from(uniqueSources).sort();
});

// 获取教材名称
const getTextbookName = (id) => {
    const textbook = textbooks.value.find(t => t.id === id);
    return textbook ? textbook.name : '';
};

// 切换节点展开状态
const toggleNode = (node) => {
    node.expanded = !node.expanded;
};

// 选择知识点
const selectPoint = (point) => {
    currentPoint.value = point.id;
};

// 过滤后的题目
const filteredQuestions = computed(() => {
    let result = questions.value;

    // 搜索过滤
    if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        result = result.filter(q =>
            q.content.toLowerCase().includes(query) ||
            q.answer.toLowerCase().includes(query) ||
            (q.explain && q.explain.toLowerCase().includes(query)) ||
            q.tags.some(tag => tag.toLowerCase().includes(query)) ||
            q.chapter.toLowerCase().includes(query) ||
            q.point.toLowerCase().includes(query)
        );
    }

    // 类型过滤
    if (filter.type !== 'all') {
        result = result.filter(q => q.type === filter.type);
    }

    // 难度过滤
    if (filter.difficulty !== 'all') {
        result = result.filter(q => q.difficulty === filter.difficulty);
    }

    // 年份过滤
    if (filter.year !== 'all') {
        result = result.filter(q => q.year === filter.year);
    }

    // 来源过滤
    if (filter.source !== 'all') {
        result = result.filter(q => q.source === filter.source);
    }

    return result;
});

// 分页数据
const paginatedQuestions = computed(() => {
    const start = (currentPage.value - 1) * pageSize.value;
    return filteredQuestions.value.slice(start, start + pageSize.value);
});

// 总页数
const totalPages = computed(() => {
    return Math.ceil(filteredQuestions.value.length / pageSize.value);
});

// 方法
const toggleAnswer = (question) => {
    question.showAnswer = !question.showAnswer;
    // 在显示答案后立即渲染数学公式
    if (question.showAnswer) {
        renderMath();
    }
};

const prevPage = () => {
    if (currentPage.value > 1) {
        currentPage.value--;
    }
};

const nextPage = () => {
    if (currentPage.value < totalPages.value) {
        currentPage.value++;
    }
};

const getDifficultyClass = (difficulty) => {
    switch (difficulty) {
        case '容易': return 'easy';
        case '较易': return 'easy';
        case '适中': return 'medium';
        case '较难': return 'hard';
        case '困难': return 'hard';
        default: return '';
    }
};

// 初始化MathJax渲染
const renderMath = () => {
    if (window.MathJax) {
        window.MathJax.typeset();
    }
};

// 加入组卷
const addToCollection = (question) => {
    if (!selectedQuestions.value.some(q => q.id === question.id)) {
        selectedQuestions.value.push(question);
        alert(`题目 #${question.id} 已添加到组卷列表！`);
    } else {
        alert('该题目已在组卷列表中！');
    }
};

// 显示组卷对话框
const showBuildPaperDialog = () => {
    if (selectedQuestions.value.length === 0) {
        alert('请先选择题目加入组卷！');
        return;
    }
    showPaperDialog.value = true;
};

// 下载试卷
const downloadPaper = () => {
    alert(`试卷 "${paperTitle.value}" 生成成功！包含 ${selectedQuestions.value.length} 道题目。`);
    showPaperDialog.value = false;
    // 实际项目中这里会有生成试卷的逻辑
};

// 监听题目变化时重新渲染数学公式
watch(paginatedQuestions, () => {
    renderMath();
});

// 初始化时渲染数学公式
onMounted(() => {
    renderMath();
});


async function fetchQuestions() {
    const params = { page: page.value, page_size: pageSize.value, ...filters.value }
    if (selectedChapter.value) params.chapter = selectedChapter.value
    const res = await axios.get('/api/questions', { params })
    // questions.value = res.data.questions
    // console.log(questions.value);
    // total.value = res.data.total
}

// onMounted(fetchQuestions)

function onFilter(f) { filters.value = f; page.value = 1; fetchQuestions() }
function onSelectChapter(id) { selectedChapter.value = id; page.value = 1; fetchQuestions() }
function onPage(p) { page.value = p; fetchQuestions() }

function onAdd(q) {
    if (!paperList.value.find(x => x.questionsId === q.questionsId)) {
        paperList.value.push(q)
        alert(`已添加到组卷：ID ${q.questionsId}`)
    }
}
</script>

<style scoped>
/* .question-bank-page {
    display: flex;
    height: 100vh;
}

.main-area {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 16px;
}

.cards {
    flex: 1;
    overflow-y: auto;
} */

* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    font-family: 'Segoe UI', 'Microsoft YaHei', sans-serif;
}

body {
    background: linear-gradient(135deg, #f8f9fc, #eef2f7);
    color: #333;
    line-height: 1.6;
    height: 100vh;
    overflow: hidden;
}

.container {
    display: grid;
    grid-template-columns: 300px 1fr;
    grid-template-rows: 70px 1fr;
    height: 100vh;
    max-width: 1800px;
    margin: 0 auto;
    box-shadow: 0 0 40px rgba(0, 0, 0, 0.08);
}

header {
    grid-column: 1 / -1;
    background: linear-gradient(135deg, #2c3e50, #1a5276);
    color: white;
    padding: 0 25px;
    display: flex;
    align-items: center;
    z-index: 10;
    box-shadow: 0 2px 15px rgba(0, 0, 0, 0.2);
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
}

.logo {
    display: flex;
    align-items: center;
    gap: 15px;
}

.logo-icon {
    font-size: 28px;
    color: #3498db;
}

h1 {
    font-size: 22px;
    font-weight: 600;
    letter-spacing: 0.5px;
}

.header-actions {
    display: flex;
    align-items: center;
    gap: 20px;
}

.textbook-selector {
    display: flex;
    align-items: center;
    gap: 15px;
}

select {
    padding: 9px 18px;
    border-radius: 8px;
    border: none;
    background: rgba(255, 255, 255, 0.15);
    color: white;
    font-weight: 500;
    min-width: 200px;
    font-size: 15px;
    backdrop-filter: blur(5px);
    transition: all 0.3s ease;
}

select:hover {
    background: rgba(255, 255, 255, 0.25);
}

select:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.5);
}

select option {
    background: #1a5276;
    color: white;
    padding: 10px;
}

.btn {
    padding: 10px 22px;
    border-radius: 8px;
    border: none;
    background: linear-gradient(135deg, #3498db, #2980b9);
    color: white;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.3s ease;
    font-size: 15px;
    box-shadow: 0 4px 12px rgba(41, 128, 185, 0.3);
}

.btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(41, 128, 185, 0.4);
}

.btn:active {
    transform: translateY(0);
}

.sidebar {
    background: white;
    border-right: 1px solid #e0e6ed;
    overflow-y: auto;
    padding: 25px 0;
    box-shadow: 5px 0 15px rgba(0, 0, 0, 0.03);
}

.textbook-title {
    padding: 0 25px 20px;
    font-size: 20px;
    font-weight: 700;
    color: #2c3e50;
    border-bottom: 1px solid #eee;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 10px;
}

.textbook-title i {
    color: #3498db;
}

.tree-container {
    padding: 0 15px;
}

.tree-node {
    margin-bottom: 8px;
    position: relative;
}

.node-content {
    padding: 10px 15px 10px 35px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    position: relative;
}

.node-content:hover {
    background: #f0f7ff;
}

.node-content.active {
    background: #e3f2ff;
    color: #1a5276;
    font-weight: 600;
}

.node-icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #3498db;
    font-size: 16px;
    transition: all 0.3s ease;
}

.toggle-icon {
    position: absolute;
    left: 5px;
    top: 50%;
    transform: translateY(-50%);
    color: #7f8c8d;
    font-size: 12px;
    transition: all 0.3s ease;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.toggle-icon.expanded {
    transform: translateY(-50%) rotate(90deg);
}

.chapter .node-content {
    font-weight: 700;
    color: #2c3e50;
    padding-left: 40px;
    background: #f8fafc;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.03);
}

.section .node-content {
    padding-left: 60px;
}

.knowledge-point .node-content {
    padding-left: 80px;
    font-size: 14px;
}

.children {
    padding-left: 30px;
    margin-left: 15px;
    border-left: 2px solid #e0e6ed;
    overflow: hidden;
    transition: all 0.3s ease;
}

.children.collapsed {
    max-height: 0;
    opacity: 0;
    margin-top: 0;
    margin-bottom: 0;
    padding: 0;
    border: none;
}

.main-content {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: #f9fafb;
}

.filters {
    background: white;
    padding: 18px 25px;
    border-bottom: 1px solid #e0e6ed;
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    align-items: center;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.04);
    z-index: 5;
}

.filter-group {
    display: flex;
    align-items: center;
    gap: 10px;
}

.filter-label {
    font-size: 15px;
    color: #555;
    font-weight: 500;
}

select.filter-select {
    padding: 10px 15px;
    border: 1px solid #ddd;
    border-radius: 8px;
    background: white;
    color: #333;
    min-width: 130px;
    font-size: 14px;
    transition: all 0.3s ease;
}

select.filter-select:hover {
    border-color: #3498db;
}

.search-container {
    position: relative;
    flex: 1;
    max-width: 350px;
}

.search-container i {
    position: absolute;
    left: 15px;
    top: 50%;
    transform: translateY(-50%);
    color: #7f8c8d;
}

.search-container input {
    width: 100%;
    padding: 10px 15px 10px 45px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 15px;
    transition: all 0.3s ease;
}

.search-container input:focus {
    outline: none;
    border-color: #3498db;
    box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.2);
}

.questions-container {
    flex: 1;
    overflow-y: auto;
    padding: 25px;
    background: #f9fafb;
}

.questions-list {
    display: flex;
    flex-direction: column;
    gap: 25px;
}

.question-card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
    padding: 25px;
    transition: all 0.3s ease;
    border-left: 4px solid #3498db;
}

.question-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.question-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 18px;
    padding-bottom: 15px;
    border-bottom: 1px solid #eee;
    align-items: flex-start;
}

.question-info {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.question-title {
    font-size: 18px;
    font-weight: 700;
    color: #2c3e50;
}

.question-meta {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
}

.meta-item {
    background: #f0f7ff;
    color: #2980b9;
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 6px;
}

.source-badge {
    background: linear-gradient(135deg, #e74c3c, #c0392b);
    color: white;
}

.meta-item.difficulty-easy {
    background: #e8f5e9;
    color: #27ae60;
}

.meta-item.difficulty-medium {
    background: #fff8e1;
    color: #f39c12;
}

.meta-item.difficulty-hard {
    background: #ffebee;
    color: #e74c3c;
}

.question-content {
    margin-bottom: 20px;
    font-size: 16px;
    line-height: 1.8;
    color: #34495e;
}

.question-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 20px;
}

.tag {
    background: #eaf7ff;
    color: #3498db;
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 13px;
}

.question-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-top: 1px solid #f0f4f8;
    padding-top: 20px;
    margin-top: 10px;
}

.action-buttons {
    display: flex;
    gap: 12px;
}

.action-btn {
    padding: 8px 16px;
    border-radius: 6px;
    border: 1px solid #e0e6ed;
    background: white;
    color: #34495e;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: all 0.3s ease;
    font-size: 14px;
}

.action-btn:hover {
    background: #f8fafc;
    border-color: #3498db;
    color: #3498db;
}

.toggle-answer {
    background: linear-gradient(135deg, #3498db, #2980b9);
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.3s ease;
    font-size: 14px;
    box-shadow: 0 4px 12px rgba(41, 128, 185, 0.3);
}

.toggle-answer:hover {
    background: linear-gradient(135deg, #2980b9, #1a5276);
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(41, 128, 185, 0.4);
}

.answer-section {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px dashed #e0e6ed;
}

.answer-title {
    font-weight: 700;
    margin-bottom: 15px;
    color: #27ae60;
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 18px;
}

.answer-content {
    background: #f9fdf9;
    padding: 20px;
    border-radius: 10px;
    border-left: 4px solid #27ae60;
    font-size: 16px;
    line-height: 1.7;
}

.pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 15px;
    margin-top: 35px;
    padding: 15px 0;
}

.pagination button {
    background: white;
    border: 1px solid #e0e6ed;
    border-radius: 8px;
    padding: 10px 18px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

.pagination button:hover:not(:disabled) {
    background: #3498db;
    color: white;
    border-color: #3498db;
    box-shadow: 0 4px 10px rgba(52, 152, 219, 0.3);
}

.pagination button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.page-info {
    font-size: 15px;
    color: #7f8c8d;
    font-weight: 500;
}

.math-content {
    padding: 12px 0;
    font-size: 17px;
    line-height: 1.8;
}

.math-formula {
    display: inline-block;
    margin: 5px 0;
    padding: 2px 5px;
}

.empty-state {
    text-align: center;
    padding: 60px 20px;
    color: #95a5a6;
}

.empty-state i {
    font-size: 60px;
    margin-bottom: 20px;
    color: #d5dbdb;
}

.empty-state h3 {
    font-size: 22px;
    margin-bottom: 15px;
    color: #7f8c8d;
}

.empty-state p {
    font-size: 16px;
    max-width: 500px;
    margin: 0 auto;
}

@media (max-width: 1200px) {
    .container {
        grid-template-columns: 260px 1fr;
    }
}

@media (max-width: 992px) {
    .container {
        grid-template-columns: 1fr;
    }

    .sidebar {
        display: none;
    }

    .filters {
        flex-direction: column;
        align-items: flex-start;
    }

    .search-container {
        max-width: 100%;
    }
}

@media (max-width: 768px) {
    .header-content {
        flex-direction: column;
        align-items: flex-start;
        gap: 15px;
        padding: 15px 0;
    }

    .header-actions {
        width: 100%;
        justify-content: space-between;
    }

    .question-header {
        flex-direction: column;
        gap: 15px;
    }

    .question-actions {
        flex-direction: column;
        gap: 15px;
        align-items: flex-start;
    }
}
</style>